import * as XLSX from 'xlsx';
import { Asset, AssetClassMeta, Bucket } from '../types';
import { getAssetCurrentValue, getAssetCostBasis } from './calculations';

// Ngưỡng cảnh báo: nếu giá trị < 1 triệu VNĐ → có thể user nhập nhầm đơn vị tỷ
const SUSPICIOUS_THRESHOLD = 1_000_000;

// ── Cột export ────────────────────────────────────────────────────────────────
const HEADERS = [
  'Tên tài sản',
  'Mã',
  'Lớp tài sản',
  'Quỹ',
  'Giá trị (VNĐ)',   // raw integer, format với thousand separator
  'Giá vốn (VNĐ)',   // raw integer, format với thousand separator
  'Yield Rate (%)',
  'Cap Gain (%)',
  'Ghi chú',
];

// Excel number format: integer với thousand separator, không thập phân
const FMT_VND  = '#,##0';
const FMT_PCT  = '0.00';

// ── Export ────────────────────────────────────────────────────────────────────
export function exportAssetsToExcel(assets: Asset[], buckets: Bucket[]) {
  const dataRows = assets.map(a => {
    const bucket = buckets.find(b => b.id === a.bucketId);
    return [
      a.name,
      a.code,
      a.assetClass,
      bucket?.name ?? 'Danh mục Chung',
      Math.round(getAssetCurrentValue(a)),  // raw VNĐ integer
      Math.round(getAssetCostBasis(a)),     // raw VNĐ integer
      a.yieldRate       ?? 0,
      a.capitalGainRate ?? 0,
      a.notes ?? '',
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...dataRows]);

  // Áp dụng format cho từng ô tiền tệ và % (SheetJS CE hỗ trợ cell.z)
  const numRows = dataRows.length;
  for (let r = 1; r <= numRows; r++) {
    // Cột E (index 4) — Giá trị VNĐ
    const cellE = XLSX.utils.encode_cell({ r, c: 4 });
    if (ws[cellE]) ws[cellE].z = FMT_VND;
    // Cột F (index 5) — Giá vốn VNĐ
    const cellF = XLSX.utils.encode_cell({ r, c: 5 });
    if (ws[cellF]) ws[cellF].z = FMT_VND;
    // Cột G (index 6) — Yield Rate
    const cellG = XLSX.utils.encode_cell({ r, c: 6 });
    if (ws[cellG]) ws[cellG].z = FMT_PCT;
    // Cột H (index 7) — Cap Gain
    const cellH = XLSX.utils.encode_cell({ r, c: 7 });
    if (ws[cellH]) ws[cellH].z = FMT_PCT;
  }

  ws['!cols'] = [
    { wch: 32 }, // Tên
    { wch: 12 }, // Mã
    { wch: 18 }, // Lớp
    { wch: 20 }, // Quỹ
    { wch: 22 }, // Giá trị VNĐ
    { wch: 22 }, // Giá vốn VNĐ
    { wch: 14 }, // Yield
    { wch: 14 }, // Cap Gain
    { wch: 36 }, // Ghi chú
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tài sản');

  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `WealthOS_TaiSan_${date}.xlsx`);
}

// ── Import / parse ────────────────────────────────────────────────────────────
export interface ImportRow {
  name: string;
  code: string;
  assetClass: string;
  bucketName: string;
  currentValueVnd: number;  // VNĐ gốc
  costBasisVnd: number;     // VNĐ gốc
  yieldRate: number;        // %
  capitalGainRate: number;  // %
  notes: string;
}

export interface ImportValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ParseResult {
  rows: ImportRow[];
  errors: ImportValidationError[];
}

export function parseAssetsFromExcel(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 2) {
          resolve({ rows: [], errors: [{ row: 0, field: 'file', message: 'File không có dữ liệu' }] });
          return;
        }

        const dataRows = raw.slice(1).filter(r => r.some((c: any) => c !== ''));
        const rows: ImportRow[] = [];
        const errors: ImportValidationError[] = [];

        dataRows.forEach((r, idx) => {
          const rowNum = idx + 2;
          const name            = String(r[0] ?? '').trim();
          const code            = String(r[1] ?? '').trim();
          const assetClass      = String(r[2] ?? '').trim();
          const bucketName      = String(r[3] ?? '').trim();
          const currentValueVnd = parseFloat(String(r[4] ?? '0').replace(/,/g, '')) || 0;
          const costBasisVnd    = parseFloat(String(r[5] ?? '0').replace(/,/g, '')) || 0;
          const yieldRate       = parseFloat(String(r[6] ?? '0').replace(/,/g, '')) || 0;
          const capitalGainRate = parseFloat(String(r[7] ?? '0').replace(/,/g, '')) || 0;
          const notes           = String(r[8] ?? '').trim();

          if (!name) {
            errors.push({ row: rowNum, field: 'Tên tài sản', message: `Hàng ${rowNum}: Tên tài sản không được để trống` });
            return;
          }
          if (isNaN(currentValueVnd) || currentValueVnd < 0) {
            errors.push({ row: rowNum, field: 'Giá trị', message: `Hàng ${rowNum}: Giá trị (VNĐ) không hợp lệ` });
            return;
          }
          // Cảnh báo nhập nhầm đơn vị: giá trị > 0 nhưng < 1 triệu → rất có thể nhập bằng tỷ
          if (currentValueVnd > 0 && currentValueVnd < SUSPICIOUS_THRESHOLD) {
            errors.push({
              row: rowNum,
              field: 'Giá trị',
              message: `Hàng ${rowNum} ("${name}"): Giá trị ${currentValueVnd.toLocaleString('vi-VN')} có vẻ quá thấp — bạn đang dùng đơn vị Tỷ hay VNĐ? Cột "Giá trị (VNĐ)" cần nhập số nguyên, ví dụ: 7150000000`,
            });
            return;
          }
          if (isNaN(yieldRate)) {
            errors.push({ row: rowNum, field: 'Yield Rate', message: `Hàng ${rowNum}: Yield Rate phải là số` });
            return;
          }
          if (isNaN(capitalGainRate)) {
            errors.push({ row: rowNum, field: 'Cap Gain', message: `Hàng ${rowNum}: Cap Gain phải là số` });
            return;
          }

          rows.push({ name, code, assetClass, bucketName, currentValueVnd, costBasisVnd, yieldRate, capitalGainRate, notes });
        });

        resolve({ rows, errors });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// ── Merge import rows vào asset list ─────────────────────────────────────────
export interface MergeResult {
  updated: number;
  inserted: number;
  nextAssets: Asset[];
}

export function mergeImportRows(
  rows: ImportRow[],
  existing: Asset[],
  buckets: Bucket[],
  assetClasses: AssetClassMeta[],
  genId: () => string,
): MergeResult {
  let updated = 0;
  let inserted = 0;
  const nextAssets = [...existing];

  rows.forEach(row => {
    const bucket = buckets.find(b => b.name.toLowerCase() === row.bucketName.toLowerCase())
                ?? buckets.find(b => b.id === 'ruong-chua');
    const cls = assetClasses.find(c => c.name.toLowerCase() === row.assetClass.toLowerCase());

    // Giá trị đã là VNĐ gốc — dùng trực tiếp (quantity=1, price=value)
    const currentPrice = row.currentValueVnd;
    const costPrice    = row.costBasisVnd;

    const existingIdx = nextAssets.findIndex(a => a.name.toLowerCase() === row.name.toLowerCase());

    if (existingIdx >= 0) {
      const orig = nextAssets[existingIdx];
      nextAssets[existingIdx] = {
        ...orig,
        code:            row.code || orig.code,
        assetClass:      cls?.name || row.assetClass || orig.assetClass,
        assetClassId:    cls?.id   || orig.assetClassId,
        bucketId:        bucket?.id || orig.bucketId,
        currentPrice,
        costPrice,
        quantity:        1,
        yieldRate:       row.yieldRate,
        capitalGainRate: row.capitalGainRate,
        notes:           row.notes || orig.notes,
      };
      updated++;
    } else {
      const newId = row.code
        ? row.code.toLowerCase().replace(/\s+/g, '-')
        : genId();
      nextAssets.push({
        id:              newId,
        name:            row.name,
        code:            row.code || newId,
        assetClass:      cls?.name || row.assetClass || 'Khác',
        assetClassId:    cls?.id   || '',
        bucketId:        bucket?.id || 'ruong-chua',
        purchaseDate:    new Date().toISOString().split('T')[0],
        currentPrice,
        costPrice,
        quantity:        1,
        currency:        'VND',
        yieldRate:       row.yieldRate,
        capitalGainRate: row.capitalGainRate,
        notes:           row.notes,
        estimatedIncome: Math.round(currentPrice * row.yieldRate / 100),
        incomeEvents:    [],
      });
      inserted++;
    }
  });

  return { updated, inserted, nextAssets };
}
